import {
  CircleAlert,
  CircleCheck,
  Info,
  RefreshCw,
  SearchX,
  ShieldX,
  Sparkles,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { t } from '@ticarishift/i18n';
import { cn } from '../lib/cn.ts';
import { Button, IconButton } from './button.tsx';

type AlertTone = 'info' | 'ok' | 'attention' | 'action' | 'danger';

const toneStyles: Record<AlertTone, { box: string; icon: LucideIcon; iconClass: string }> = {
  info: { box: 'border-status-info bg-status-info-bg', icon: Info, iconClass: 'text-status-info' },
  ok: { box: 'border-status-ok bg-status-ok-bg', icon: CircleCheck, iconClass: 'text-status-ok' },
  attention: { box: 'border-status-attention bg-status-attention-bg', icon: TriangleAlert, iconClass: 'text-status-attention' },
  action: { box: 'border-status-action bg-status-action-bg', icon: CircleAlert, iconClass: 'text-status-action' },
  danger: { box: 'border-status-danger bg-status-danger-bg', icon: CircleAlert, iconClass: 'text-status-danger' },
};

type InlineAlertProps = {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Kapatılabilir uyarı. */
  onDismiss?: () => void;
  action?: ReactNode;
  /** `banner`: sayfa üstünde tam genişlik şerit (ör. "Bağlantı yok"). */
  variant?: 'inline' | 'banner';
  className?: string;
};

/**
 * Sayfa içi uyarı ve şerit (08 §6 #36). Tehlike tonu `role="alert"` ile, diğerleri `role="status"` ile
 * duyurulur; renk ikon ve metinle birlikte gelir.
 */
export function InlineAlert({ tone = 'info', title, children, onDismiss, action, variant = 'inline', className }: InlineAlertProps) {
  const style = toneStyles[tone];
  const Icon = style.icon;
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 border text-body text-fg',
        variant === 'banner' ? 'w-full border-x-0 border-t-0 px-4 py-2.5' : 'rounded-lg px-4 py-3',
        style.box,
        className,
      )}
    >
      <Icon aria-hidden size={20} strokeWidth={1.75} className={cn('mt-px shrink-0', style.iconClass)} />
      <div className="grid min-w-0 flex-1 gap-0.5">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className="text-body-sm text-fg">{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
      {onDismiss && (
        <IconButton label={t('common.actions.dismiss')} icon={<X size={16} strokeWidth={1.75} />} size="sm" className="-me-2 -mt-1" onClick={onDismiss} />
      )}
    </div>
  );
}

type EmptyStateVariant = 'first-use' | 'no-results' | 'forbidden' | 'positive';

const emptyIcons: Record<EmptyStateVariant, LucideIcon> = {
  'first-use': Sparkles,
  'no-results': SearchX,
  forbidden: ShieldX,
  positive: CircleCheck,
};

type EmptyStateProps = {
  /**
   * İlk kullanım: burada ne olur + nasıl başlanır + CTA. Sonuç yok: ne arandı + çıkış yolu. Yetki yok:
   * neden + kimden istenir. Olumlu boşluk: başarı + bağlam (08 §6.1).
   */
  variant: EmptyStateVariant;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  icon?: LucideIcon;
  /**
   * Başlık düzeyi sayfadaki yerine göre (sayfada tek h1): sayfanın kendisi olan durumda (404, 403, kayıt bulunamadı)
   * 1'dir, sayfa başlığının altındaki blokta 2 ya da 3.
   */
  headingLevel?: 1 | 2 | 3;
  className?: string;
};

export function EmptyState({ variant, title, body, action, icon, headingLevel = 2, className }: EmptyStateProps) {
  const Icon = icon ?? emptyIcons[variant];
  const Heading = (['h1', 'h2', 'h3'] as const)[headingLevel - 1]!;
  return (
    <div className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
      <span
        className={cn(
          'inline-flex size-12 items-center justify-center rounded-full',
          variant === 'positive' ? 'bg-status-ok-bg text-status-ok' : 'bg-surface-muted text-fg-muted',
        )}
      >
        <Icon aria-hidden size={24} strokeWidth={1.75} />
      </span>
      <Heading className="text-h3 text-fg">{title}</Heading>
      {body && <p className="max-w-md text-body text-fg-muted">{body}</p>}
      {action && <div className="pt-2">{action}</div>}
    </div>
  );
}

type ErrorStateProps = {
  onRetry?: () => void;
  /** Destek talebinde istenen kod (ör. RFC 9457 `traceId`'nin kısa hâli). */
  supportCode?: string;
  className?: string;
};

/** Rota hatası (08 §6 #49, §11.7): "Bu sayfa açılamadı"; tekrar dene ve ana sayfa. */
export function RouteError({ onRetry, supportCode, homeHref = '/', className }: ErrorStateProps & { homeHref?: string }) {
  return (
    <div role="alert" className={cn('mx-auto flex max-w-lg flex-col items-center gap-3 px-6 py-16 text-center', className)}>
      <span className="inline-flex size-12 items-center justify-center rounded-full bg-status-danger-bg text-status-danger">
        <CircleAlert aria-hidden size={24} strokeWidth={1.75} />
      </span>
      <h1 className="text-h1 text-fg">{t('common.errorBoundary.routeTitle')}</h1>
      <p className="text-body text-fg-muted">{t('common.errorBoundary.routeBody')}</p>
      {supportCode && (
        <p className="font-mono text-body-sm text-fg-muted">{t('common.errorBoundary.supportCode', { code: supportCode })}</p>
      )}
      <div className="flex flex-wrap justify-center gap-2 pt-2">
        {onRetry && (
          <Button icon={<RefreshCw size={16} strokeWidth={1.75} />} onClick={onRetry}>
            {t('common.actions.retry')}
          </Button>
        )}
        <Button variant="secondary" asChild>
          <a href={homeHref}>{t('common.actions.goHome')}</a>
        </Button>
      </div>
    </div>
  );
}

/** Blok hatası: kartlar ve paneller birbirinden bağımsız hata verir (08 §11.7). */
export function BlockError({ onRetry, supportCode, className }: ErrorStateProps) {
  return (
    <div role="alert" className={cn('flex flex-col items-start gap-2 rounded-lg border border-status-danger bg-status-danger-bg p-4', className)}>
      <p className="flex items-center gap-2 font-medium text-fg">
        <CircleAlert aria-hidden size={18} strokeWidth={1.75} className="text-status-danger" />
        {t('common.errorBoundary.blockTitle')}
      </p>
      <p className="text-body-sm text-fg">{t('common.errorBoundary.blockBody')}</p>
      {supportCode && (
        <p className="font-mono text-caption text-fg-muted">{t('common.errorBoundary.supportCode', { code: supportCode })}</p>
      )}
      {onRetry && (
        <Button variant="secondary" size="sm" icon={<RefreshCw size={14} strokeWidth={1.75} />} onClick={onRetry}>
          {t('common.actions.retry')}
        </Button>
      )}
    </div>
  );
}
